import React, { useEffect, useRef, useState } from "react";
import { Box, styled, IconButton, Typography } from "@mui/material";
import { VolumeOff, VolumeUp } from "@mui/icons-material";

const VideoScreen = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  overflow: "hidden",
});

const VideoContainer = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  overflow: "hidden",
  zIndex: 3,
});

const AnimatedVideo = styled("video")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "10px",
  filter: "brightness(1.1) contrast(1.15) saturate(1.1)",
  position: "relative",
  zIndex: 4,
});

const VideoFrame = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-20px",
    left: "-20px",
    right: "-20px",
    bottom: "-20px",
    background:
      "linear-gradient(45deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)",
    borderRadius: "25px",
    filter: "blur(25px)",
    zIndex: -1,
  },
});

const SkipButton = styled(Box)({
  position: "absolute",
  bottom: "30px",
  right: "30px",
  background: "rgba(0, 0, 0, 0.6)",
  color: "white",
  padding: "12px 24px",
  borderRadius: "25px",
  cursor: "pointer",
  zIndex: 10,
  fontSize: "16px",
  fontWeight: "bold",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(0, 0, 0, 0.8)",
    transform: "scale(1.05)",
  },
});

const VolumeButton = styled(IconButton)({
  position: "absolute",
  top: "30px",
  right: "30px",
  background: "rgba(0, 0, 0, 0.6)",
  color: "white",
  zIndex: 10,
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  "&:hover": {
    background: "rgba(0, 0, 0, 0.8)",
  },
});

const ClickMessage = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  background: "rgba(0, 0, 0, 0.7)",
  color: "white",
  padding: "20px 30px",
  borderRadius: "15px",
  textAlign: "center",
  zIndex: 11,
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
});

const VideoIntro = ({ onVideoEnd, onSkip }) => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [showClickMessage, setShowClickMessage] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);

  useEffect(() => {
    const initializeVideo = async () => {
      if (videoRef.current) {
        try {
          // Start with muted autoplay (this is allowed by browsers)
          videoRef.current.muted = true;
          videoRef.current.volume = 0;

          await videoRef.current.play();
          console.log("Video started playing (muted)");
          setVideoStarted(true);

          // Show message to click for audio
          setShowClickMessage(true);
          setTimeout(() => setShowClickMessage(false), 3000);
        } catch (error) {
          console.log("Video auto-play prevented:", error);
        }
      }
    };

    initializeVideo();

    const timer = setTimeout(() => {
      onVideoEnd();
    }, 18000);

    return () => {
      clearTimeout(timer);
    };
  }, [onVideoEnd]);

  const handleUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.volume = 1.0;
      setIsMuted(false);
      setShowClickMessage(false);
    }
  };

  const handleToggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const handleVideoClick = (e) => {
    // If video hasn't started or is muted, try to unmute on click
    if (!videoStarted || isMuted) {
      handleUnmute();
    } else {
      onSkip();
    }
  };

  const handleSkipClick = (e) => {
    e.stopPropagation();
    onSkip();
  };

  return (
    <VideoScreen onClick={handleVideoClick}>
      <VideoContainer>
        <VideoFrame>
          <AnimatedVideo
            ref={videoRef}
            autoPlay
            muted={isMuted}
            playsInline
            preload="auto"
            loop={false}
            onEnded={onVideoEnd}
          >
            <source src="/videos/new-cop30-gop.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </AnimatedVideo>

          {/* Volume Control Button */}
          <VolumeButton onClick={handleToggleMute}>
            {isMuted ? <VolumeOff /> : <VolumeUp />}
          </VolumeButton>

          {/* Click to unmute message */}
          {showClickMessage && (
            <ClickMessage>
              <Typography variant="body2">
                🔇 Click anywhere to enable sound
              </Typography>
            </ClickMessage>
          )}

          {/* Initial click to start message */}
          {!videoStarted && (
            <ClickMessage>
              <Typography variant="h6" sx={{ mb: 1 }}>
                ▶️ Click to Start
              </Typography>
              <Typography variant="body2">
                Click anywhere to begin the video
              </Typography>
            </ClickMessage>
          )}
        </VideoFrame>
      </VideoContainer>
    </VideoScreen>
  );
};

export default VideoIntro;
